/**
 * "Under construction" notice for the Dashboard.
 *
 * Config-driven per the repo convention (runtime config in
 * `public/config.<env>.json`, never hardcoded): the banner is controlled by
 * the optional `underConstruction` block, so it can be switched off — or its
 * copy reworded — at deploy time without a rebuild. Absent block → shown,
 * because the dashboard IS still under construction; a deploy that wants it
 * gone must say so explicitly.
 *
 * Dismissal is per-browser-session (sessionStorage): it stops nagging while
 * someone works, but comes back on their next visit so the caveat keeps
 * setting expectations rather than being permanently clicked away.
 */
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";

import { configService } from "../services/configService";

const DISMISS_KEY = "mttr.underConstruction.dismissed";

export const DEFAULT_MESSAGE =
  "This dashboard is still under construction — figures may be incomplete or " +
  "shift as sources are wired up. Don't treat them as final.";

const UnderConstructionBanner: React.FC = () => {
  const cfg = configService.getConfig().underConstruction;
  // Unset → show. Only an explicit `enabled: false` hides it.
  const enabled = cfg?.enabled !== false;

  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      // Private-mode / storage-disabled browsers: just show it.
      return false;
    }
  });

  const dismiss = (): void => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* non-fatal — the banner still hides for this render */
    }
  };

  if (!enabled || dismissed) return null;

  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-2.5 rounded border border-warning-300 bg-warning-50 px-3 py-2 text-xs text-warning-900"
    >
      <ExclamationTriangleIcon
        className="h-4 w-4 shrink-0 text-warning-600 mt-px"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <span className="font-semibold uppercase tracking-wide mr-1.5">
          Under construction
        </span>
        {cfg?.message ?? DEFAULT_MESSAGE}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss under-construction notice"
        title="Hide until next visit"
        className="shrink-0 rounded p-0.5 text-warning-700 hover:bg-warning-100 active:bg-warning-200"
      >
        <XMarkIcon className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
};

export default UnderConstructionBanner;
